/**  MODIFIED COPY OF src/common-app/state/modules/engines/voice-mask/saga.ts TODO find way to reuse common code! */
import {
  all,
  call,
  cancel,
  fork,
  put,
  race,
  select,
  takeEvery,
  take,
  delay,
} from 'typed-redux-saga/macro';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { OUT_CREATE_JOB_FAIL, OUT_CREATE_JOB_SUCCESS } from '@worker';

import { STOP_ALL_ENGINE_POLLING } from '../actions';
import * as Actions from './actions';
import { selectEngineStatus } from './selectors';
import {
  checkJobStatusService,
  checkTaskStatusService,
  createEngineJobService,
} from './services';
import { FAILED_STATUSES, JobState } from './store';
import { TDOId } from '../../universal/models/Brands';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { sagaIntl } from '@common/i18n';

function* onProcessEngineRequestAction() {
  yield* takeEvery(Actions.PROCESS_ENGINE_REQUEST, function* (action) {
    yield* startEngineJob(action.payload);
  });
}

export function* onProcessEngineRequestSuccessAction() {
  yield* takeEvery(
    Actions.PROCESS_ENGINE_REQUEST_SUCCESS,
    onHandleEngineRequestSuccessAction
  );
}

export function* onHandleEngineRequestSuccessAction() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      autoHideDuration: 10_000,
      message: intl.formatMessage({
        id: 'processSpeakerSeparationSuccess',
        defaultMessage: 'Speaker separation is now ready.',
      }),
    })
  );
}

function* onCreateEngineJobAction() {
  yield* takeEvery(Actions.CREATE_ENGINE_JOB, function* (action) {
    yield* put(createEngineJobService(action.payload));
  });
}

function* onStartPollingEngineResultsAction() {
  yield* takeEvery(Actions.START_POLLING_ENGINE_RESULTS, function* (action) {
    yield* call(startPollingJobStatus, action.payload);
  });
}

function* onCheckJobStatusAction() {
  yield* takeEvery(Actions.CHECK_JOB_STATUS, function* (action) {
    yield* put(checkJobStatusService(action.payload));
  });
}

function* onCheckTaskStatusAction() {
  yield* takeEvery(Actions.CHECK_TASK_STATUS, function* (action) {
    yield* put(checkTaskStatusService(action.payload));
  });
}

/**
 * Report a failure the poller saw, whichever status reply carried it.
 *
 * Both replies route through here: a run started from the editor is polled by
 * job, one picked up from the upload DAG by task (VE-30784). Watching only the
 * job reply left a DAG run's failure entirely silent — the loop cancelled and
 * the user was told nothing.
 */
function* reportFailedStatus(status: JobState['status']) {
  if (!FAILED_STATUSES.includes(status)) {
    return;
  }
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage(
        {
          id: 'processSpeakerSeparationFailed',
          defaultMessage:
            'Speaker separation {status}. Please try again or contact support.',
        },
        { status }
      ),
      variant: 'error',
    })
  );
}

function* onCheckJobStatusSuccessAction() {
  yield* takeEvery(Actions.CHECK_JOB_STATUS_SUCCESS, function* (action) {
    yield* reportFailedStatus(action.payload.job.status);
  });
}

function* onCheckTaskStatusSuccessAction() {
  yield* takeEvery(Actions.CHECK_TASK_STATUS_SUCCESS, function* (action) {
    yield* reportFailedStatus(action.payload.task.status);
  });
}

export const startEngineJob = function* ({
  tdoId,
  tdoName,
  payload,
  retryCount = 3,
}: {
  tdoId: TDOId;
  tdoName: string;
  payload: {
    targetId: string;
    url: string;
  };
  retryCount?: number;
}): any {
  yield* put(Actions.createEngineJobAction({ tdoId, tdoName, payload }));
  const { ok, err } = yield* race({
    ok: take(Actions.CREATE_ENGINE_JOB_SUCCESS),
    err: take(Actions.CREATE_ENGINE_JOB_FAILURE),
  });

  if (err && retryCount <= 0) {
    yield* put(OUT_CREATE_JOB_FAIL(err));
    yield* cancel();
  }

  if (err && retryCount > 0) {
    yield* delay(3000);
    yield* startEngineJob({
      tdoId,
      tdoName,
      payload,
      retryCount: retryCount - 1,
    });
  }

  if (ok) {
    yield* put(
      Actions.startPollingEngineResultsAction({
        tdoId,
        jobId: ok.payload.createJob.id,
      })
    );
    yield* put(OUT_CREATE_JOB_SUCCESS(ok));
  }
};

const POLL_INTERVAL_MS = 10_000;
/**
 * How long to keep asking before giving up on a job that is neither finishing
 * nor reporting a failure.
 *
 * The loop used to be unbounded: a job that stalled in `running` was polled
 * every 10s for the rest of the session, one GraphQL request at a time, with
 * nothing to stop it but closing the media. An hour is well past any real run
 * and still bounds the cost of one that never ends.
 *
 * Giving up here only means we stop watching — the job may still finish, and
 * reopening the media picks the results up — so the message says that rather
 * than reporting a failure we have not observed.
 */
const MAX_POLL_DURATION_MS = 60 * 60 * 1000;

export function* startPollingJobStatus({
  jobId,
  taskId,
  tdoId,
}: {
  jobId: string;
  taskId?: string;
  tdoId: TDOId;
}) {
  const giveUpAt = Date.now() + MAX_POLL_DURATION_MS;

  while (true) {
    const { isRunning, status, error } = yield* select(
      selectEngineStatus(tdoId, jobId)
    );

    if (status === 'complete') {
      break;
    }
    if (!isRunning || error) {
      yield* put(Actions.stopPollingEngineResultsAction({ tdoId, jobId }));
      yield* cancel();
    }

    if (Date.now() >= giveUpAt) {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'processSpeakerSeparationStillRunning',
            defaultMessage:
              'Speaker separation is taking longer than expected. It may still finish — reopen this media later to check.',
          }),
        })
      );
      yield* put(Actions.stopPollingEngineResultsAction({ tdoId, jobId }));
      yield* cancel();
      return;
    }

    // A run picked up from the upload DAG shares its job with every other
    // upload engine, so the job only completes once they all do. Ask its own
    // task instead (VE-30784) — the same reasoning transcription and voice
    // mask already apply.
    if (taskId) {
      yield* put(Actions.checkTaskStatusAction({ tdoId, taskId, jobId }));
    } else {
      yield* put(Actions.checkJobStatusAction({ tdoId, jobId }));
    }

    const { stop } = yield* race({
      ok: delay(POLL_INTERVAL_MS),
      stop: take([
        isActionOfTDO(Actions.STOP_POLLING_ENGINE_RESULTS, jobId),
        STOP_ALL_ENGINE_POLLING,
      ]),
    });
    if (stop) {
      yield* put(Actions.stopPollingEngineResultsAction({ tdoId, jobId }));
      yield* cancel();
      return;
    }
  }

  yield* put(Actions.processEngineRequestSuccessAction({ tdoId, jobId }));
}

export const isActionOfTDO =
  <P>(
    action: ActionCreatorWithPayload<P extends { jobId: string } ? P : never>,
    jobId: string
  ) =>
  ({ type, payload }: { type: string; payload?: { jobId?: string } }) =>
    type === action.type && payload?.jobId === jobId;

export const saga = function* () {
  yield* all([
    fork(onProcessEngineRequestAction),
    fork(onProcessEngineRequestSuccessAction),
    fork(onCreateEngineJobAction),
    fork(onStartPollingEngineResultsAction),
    fork(onCheckJobStatusAction),
    fork(onCheckTaskStatusAction),
    fork(onCheckJobStatusSuccessAction),
    fork(onCheckTaskStatusSuccessAction),
  ]);
};
